<template>
	<!-- Main Container -->
	<div class="main-container">
		<div class="lesson-prep-page">
			<!-- Hero Section -->
			<section class="hero-section">
				<div class="hero-badge">
					<span class="hero-badge-dot"></span>
					<span>中国-东盟建筑工程技术专业AI智能备课</span>
				</div>

				<h1 class="hero-title">AI智能备课系统</h1>
				<p class="hero-subtitle">基于中国-东盟建筑工程技术专业知识库，为教师提供高效便捷的备课解决方案</p>
			</section>

			<!-- Main Content Card -->
			<div class="content-card">
				<div class="content-header">
					<h1 class="content-title">开始备课</h1>
					<p class="content-subtitle">选择课程、选择教材，3分钟完成备课资源生成</p>
				</div>

				<div class="content-body">
					<div class="step-form">
						<!-- Step 1: Course Selection -->
						<div class="form-step" :class="{ active: currentStep === 1, completed: currentStep > 1}">
							<div class="step-header">
								<div class="step-number">1</div>
								<h2 class="step-title">选择课程</h2>
							</div>

							<div class="form-group">
								<label class="form-label">课程分类</label>
								<el-select class="form-el-select" v-model="selectedCategory" @change="loadCourses">
									<el-option :value="item.id" v-for="item in categoryOptions" :key="item.id"
										:label="item.originalName">{{item.originalName}}</el-option>
								</el-select>
							</div>

							<div class="course-list">
								<div v-for="course in courseList" :key="course.id" class="course-item"
									:class="{ selected: selectedCourse && selectedCourse.id === course.id }"
									@click="selectCourse(course)">
									<div class="course-header">
										<div>
											<div class="course-name">{{ course.originalName }}</div>
											<div class="course-code">{{ course.category }}</div>
										</div>
									</div>
									<div class="course-desc">{{ course.content }}</div>
									<div class="course-stats">
										<span>📚 {{ course.fileNum || 0 }} 个可用教材</span>
									</div>
								</div>
							</div>
						</div>

						<!-- Step 2: Material Selection -->
						<div class="form-step" :class="{ active: currentStep === 2, completed: currentStep > 2 }"
							v-show="currentStep >= 2">
							<div class="step-header">
								<div class="step-number">2</div>
								<h2 class="step-title">选择教材</h2>
							</div>

							<div class="material-selection" v-if="selectedCourse">
								<div class="form-group">
									<label class="form-label">选择教材来源</label>
								</div>

								<div class="material-option" :class="{ selected: materialOption === 'default' }"
									@click="selectMaterialOption('default')">
									<div class="material-option-header">
										<div class="material-option-title">使用系统默认教材</div>
										<div v-if="materialOption === 'default'" style="color: var(--primary);">✓
										</div>
									</div>
									<div class="material-option-desc">使用已配置的课程默认教材，无需上传</div>
								</div>

								<div class="material-option" :class="{ selected: materialOption === 'upload' }"
									@click="selectMaterialOption('upload')">
									<div class="material-option-header">
										<div class="material-option-title">自定义上传教材</div>
										<div v-if="materialOption === 'upload'" style="color: var(--primary);">✓
										</div>
									</div>
									<div class="material-option-desc">上传自己的教材文件，可选择保存为默认教材</div>
								</div>

								<!-- <div class="material-option" :class="{ selected: materialOption === 'mixed' }"
										@click="selectMaterialOption('mixed')">
										<div class="material-option-header">
											<div class="material-option-title">混合使用</div>
											<div v-if="materialOption === 'mixed'" style="color: var(--primary);">✓
											</div>
										</div>
										<div class="material-option-desc">既使用默认教材，也上传自定义教材</div>
									</div> -->

								<div class="material-details" v-if="materialOption !== 'upload'">
									<div v-if="materials.length === 0"
										style="text-align: center; padding: 40px; color: var(--text-secondary);">
										<div style="font-size: 48px; margin-bottom: 16px;">📚</div>
										<div>该课程暂无默认教材</div>
									</div>
									<div v-else>
										<div style="margin-bottom: 16px;">
											<label class="form-label">可用的默认教材 ({{ materials.length }}个)</label>
										</div>
										<!-- selected -->
										<div v-for="(material,index) in materials" :key="material.id"
											class="material-option " style="margin-bottom: 8px;"
											:class="{ selected: materialIndex == index}"
											@click="selectedMaterial(material,index)">
											<div class="material-option-header">
												<div class="material-option-title">{{ material.fileName }}</div>
												<div style="font-size: 12px; color: var(--text-secondary);">
													{{ material.mimeType }} • {{ formatFileSize(material.fileSize) }}
												</div>
											</div>
											<div class="material-option-desc">{{ material.content }}</div>
										</div>
									</div>
								</div>

								<div style="margin-top: 20px;" v-if="materialOption !== 'default'">
									<label class="form-label">上传自定义教材</label>

									<el-upload class="upload-area" drag :auto-upload="false" :show-file-list="false"
										:on-change="handleFileChange" accept=".pdf,.docx,.doc,.ppt,.pptx" :limit="1" :on-exceed="handleExceed"
										action="#" ref="uploadRef">
										<div class="upload-icon">📄</div>
										<div class="upload-text">点击上传或拖拽文件到此区域</div>
										<div class="upload-hint">支持 PDF、WORD、PPT格式，单个文件不超过 50MB</div>
										<button type="button" class="btn btn-primary">
											选择文件
										</button>
									</el-upload>
									<p v-if="selectedFile" style="margin-top: 10px;">
										已选择文件：
										<span>{{ selectedFile.name }}</span>
									</p>
								</div>
							</div>
						</div>

						<!-- Step 3: Resource Generation -->
						<div class="form-step" :class="{ active: currentStep === 3, completed: currentStep > 3 }"
							v-show="currentStep >= 3">
							<div class="step-header">
								<div class="step-number">3</div>
								<h2 class="step-title">生成教学资源</h2>
							</div>

							<div class="generation-area">

								<div v-if="generationStatus!='解析完成'" class="generation-status"><i
										class="el-icon-loading"></i>{{ generationStatus }}</div>
								<div v-else class="generation-status"><i class="el-icon-check"
										style="font-size: 40px;"></i>解析完成</div>


								<div class="progress-container">
									<div class="progress-header">
										<div class="progress-title">生成进度</div>
										<div class="progress-percent">{{ Math.floor(generationProgress) }}%</div>
									</div>
									<div class="progress-bar">
										<div class="progress-fill" :style="{ width: generationProgress + '%' }">
										</div>
									</div>
									<div class="progress-details">
										<div class="time-estimate">
											<span>⏱️</span>
											<span>预计剩余时间: 3分钟</span>
										</div>
										<div class="speed-indicator">
											<span>⚡</span>
											<span>处理速度: 高速</span>
										</div>
									</div>
								</div>

								<div class="ai-visualization">
									<div class="ai-brain">🧠</div>
									<div class="ai-status">{{generationStatus!='解析完成'?'深度学习中...':'深度学习完毕'}}</div>
									<div class="ai-description" v-if="generationStatus!='解析完成'">
										AI正在分析教材结构，提取核心知识点，构建教学逻辑框架</div>
								</div>
								<div class="generation-steps">
									<div v-for="(step, index) in generationSteps" :key="index" class="generation-step">
										<span class="step-icon">{{ step.icon }}</span>
										<span class="step-text">{{ step.text }}</span>
										<span class="step-time">约{{ step.duration }}秒</span>
									</div>
								</div>
							</div>
						</div>

						<!-- Step 4: Download Results -->
						<div class="form-step" :class="{ active: currentStep === 4 }" v-show="currentStep >= 4">
							<div class="step-header">
								<div class="step-number">4</div>
								<h2 class="step-title">下载资源</h2>
							</div>

							<div class="results-grid">
								<div v-for="result in generationResults" :key="result.id" class="result-card">
									<span class="result-icon">{{ result.icon }}</span>
									<h3 class="result-title">{{ result.title }}</h3>
									<p class="result-desc">{{ result.desc }}</p>
									<div class="result-actions">
										<button class="action-button primary" @click="downloadFile(result.url)"
											v-if="result.url">
											<span>📥</span>
											下载 {{ result.format }}
										</button>
										<button class="action-button secondary" @click="previewFile(result,false)"
											v-if="result.url">
											<span>👁️</span>
											预览
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="form-actions">
						<button class="btn btn-secondary" v-show="currentStep > 1" @click="previousStep">
							← 上一步
						</button>
						<button class="btn btn-primary" @click="nextStep" :disabled="!canProceed">
							{{ getNextButtonText() }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>

</template>

<script>
	export default {
		name: 'AILessonPrepSystem',
		components: {},
		data() {
			return {
				currentStep: 1,
				categoryOptions: [], //课程分类
				selectedCategory: '', //选中课程
				selectedCourse: null,
				courseList: [], //课程
				materials: [], //教材
				materialIndex: 0, //教材选中
				filepath: '', //选中的教材
				materialOption: 'default', //选择教材OR自定义
				generationStatus: 'AI正在智能分析教材内容...',
				selectedFile: null, //自定义上传的文件
				generationProgress: 0,
				//downloadUrl: '',
				generationSteps: [{
						icon: '🔍',
						text: '解析教材内容...',
						duration: 30
					},
					{
						icon: '🧠',
						text: 'AI智能分析...',
						duration: 45
					},
					{
						icon: '📊',
						text: '生成PPT课件...',
						duration: 40
					},
					{
						icon: '📋',
						text: '生成课程标准...',
						duration: 30
					},
					{
						icon: '📝',
						text: '生成教学教案...',
						duration: 25
					},
					{
						icon: '✅',
						text: '完成资源打包...',
						duration: 10
					}
				],
				generationResults: [{
						id: 1,
						icon: '📊',
						title: 'PPT课件',
						desc: '包含完整的教学内容，精美的设计模板，适合课堂教学使用',
						format: 'PPTX',
						url: "",
					},
					{
						id: 2,
						icon: '📋',
						title: '课程标准',
						desc: '符合教育规范的课程标准文档，明确教学目标和要求',
						format: 'DOCX',
						url: "",
					},
					{
						id: 3,
						icon: '📝',
						title: '课程教案',
						desc: '详细的教学设计方案，包含教学过程和重难点分析',
						format: 'DOCX',
						url: "",
					}
				],
				progressInterval: null,
			}
		},
		computed: {
			canProceed() {
				switch (this.currentStep) {
					case 1:
						return this.selectedCourse !== null
					case 2:
						return this.materialOption !== null
					case 3:
						return this.generationProgress >= 100
					default:
						return true
				}
			}
		},
		mounted() {
			this.courseClassification();
			console.log(this.$store.state.user.userId)
		},
		// 组件销毁前清理定时器
		beforeDestroy() {},
		methods: {
			// 课程分类
			courseClassification() {
				this.HttpUtils.get('/api/system/gjfilecategory/level1', {

				}).then(result => {
					this.categoryOptions = result.data.data;
					this.selectedCategory = result.data.data[0].id;
					this.categoryFiles(this.selectedCategory)
				})
			},
			categoryFiles(id) {
				this.HttpUtils.get('/api/system/gjfilecategory/subcategory/' + id, {}).then(result => {
					this.courseList = result.data.data;
				})
			},
			loadCourses(val) { //切换课程分类
				this.categoryFiles(val)
			},

			selectCourse(course) { //查询课程下的教材
				this.selectedCourse = course;
				this.materialIndex = 0;
				this.HttpUtils.get(`/api/system/gjfilecategory/subcategory/files/${course.id}`, {}).then(result => {
					const data = result.data.data;
					if (data.length == 0) {
						return false
					}
					console.log('---', data)
					this.materials = data.filter(file => file.mimeType === 'application/pdf');
					console.log('9999', this.materials)
					if (this.materials.length == 0) {
						return false
					}
					this.filepath = this.materials[0].filePath;
				})
			},
			selectedMaterial(item, index) {
				this.materialIndex = index;
				this.filepath = item.filePath;
			},
			selectMaterialOption(option) {
				this.materialOption = option
			},
			formatFileSize(bytes) {
				if (bytes === 0) return '0 Bytes'
				const k = 1024
				const sizes = ['Bytes', 'KB', 'MB', 'GB']
				const i = Math.floor(Math.log(bytes) / Math.log(k))
				return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
			},
			// 文件选择
			handleFileChange(file) {
				// 每次选择新文件，都先重置所有状态
				this.resetState();
				// if (!file.raw || file.raw.type !== 'application/pdf') {
				// 	this.selectedFile = null;
				// 	this.$message.error('请选择 PDF 文件！');
				// 	return;
				// }

				const allowedTypes = [
					'application/pdf',
					'application/msword', // .doc
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
					'application/vnd.ms-powerpoint', // .ppt
					'application/vnd.openxmlformats-officedocument.presentationml.presentation' // .pptx
				]

				if (!allowedTypes.includes(file.raw.type)) {
					this.$message({
						message: '不支持的文件格式，请上传 PDF、WORD、PPT 文件',
						type: 'warning'
					});
					return false
				}

				// 如果文件有效，则更新文件状态，并清除错误
				this.selectedFile = file.raw;
			},
			resetState() {
				this.selectedFile = null;
				this.generationProgress = 0;
			},
			// 超出文件限制
			handleExceed() {
				this.$message.warning('只能上传一个文件，请先删除当前文件再上传。');
			},
			// 模拟进度条
			startProgress() {
				this.generationProgress = 0;
				const interval = setInterval(() => {
					if (this.generationProgress < 90) {
						// 使用 toFixed(2) 并转换为数字，保留2位小数
						this.generationProgress = parseFloat((this.generationProgress + Math.random() * 5).toFixed(
							2));
					} else {
						clearInterval(interval);
					}
				}, 500);
				return interval;
			},
			// 生成 PPT
			async generatePpt(type, blob) {
				if (!this.selectedFile && type == 1) {
					this.$message.error('请先选择 PDF 文件');
					return;
				}
				this.currentStep = 3;
				const formData = new FormData();
				this.startProgress();
				if (type == 2) { //默认教材
					formData.append('file', blob, 'example.pdf'); // 文件名设为 example.pdf
					this.cozeFilesUpload(formData)
				} else { //自定义上传教材
					formData.append('file', this.selectedFile);
					this.cozeFilesUpload(formData)
					if (this.selectedFile.type != 'application/pdf') {
						return false
					}
				}
				try {
					const {
						data
					} = await this.HttpUtils.post('/api/ppt/generate', formData, {
						headers: {
							'Content-Type': 'multipart/form-data'
						},
						timeout: 120000
					});
					this.currentStep = 4;
					this.generationResults[0].url = data.downloadUrl || '';
					this.$notify({
						title: '成功',
						message: 'PPT已生成，可下载！',
						type: 'success',
						duration: 5000,
					});

				} catch (error) {
					//clearInterval(this.progressInterval);
					//this.generationProgress = 0;
					//this.currentStep = 1;
					const errorMessage = error.response?.data?.message || error.message || '未知错误';
					this.$notify({
						title: '失败',
						message: `PPT生成失败：${errorMessage}`,
						type: 'error',
						duration: 0,
					});
				}

			},
			//上传文件到coze
			async cozeFilesUpload(formData) {
				try {
					let fileId, that = this;
					fileId = await that.HttpUtils.postcoze('/v1/files/upload', formData);
					console.log('------------', fileId)
					//2. 调用扣子API生成课标
					that.callCozeCurriculumStandards(fileId.data.data.id);
					setTimeout(function() {
						// 2. 调用扣子API生成教案
						that.callCozeTeachingAgent(fileId.data.data.id);
					}, 3000);
				} catch (error) {

				}
			},

			/***************************** 调用课标智能体开始 ***************************************/
			async callCozeCurriculumStandards(fileId) {
				// 构建请求数据 - 使用通用提示词
				const testData = {
					"stream": false,
					"user_id": "9000001",
					"bot_id": "7559956128114311231",
					"enable_card": false,
					"publish_status": "published_online",
					"additional_messages": [{
						"role": "user",
						"type": "question",
						"content_type": "object_string",
						"meta_data": {},
						"content": `[{"type":"file","file_id":"${fileId}"},{"type":"text","text":"请根据我提供的文件内容，生成一份符合教育规范的课程标准文档，明确教学目标和要求。请确保课程标准内容专业、实用，并紧密围绕文件内容。"}]`
					}],
					"auto_save_history": true
				}
				const response = await this.HttpUtils.postcoze('/v3/chat', testData);
				console.log('扣子课标智能体', response.data.data)
				if (response.data.code == 0) {
					this.startPollingStandards(response.data.data.conversation_id, response.data.data.id)
				} else {
					this.$message.error('错了哦');
				}
			},
			startPollingStandards(conversation_id, chat_id) {
				const timer = setInterval(() => {
					//轮询查看对话详情status=='completed'
					this.HttpUtils.getcoze('/v3/chat/retrieve', {
						conversation_id: conversation_id,
						chat_id: chat_id
					}).then(result => {
						// 判断是否有结果（根据你的API实际返回结构调整条件）
						if (result && result.data.data.status == 'completed') { // 这里根据你的API返回结构调整
							clearInterval(timer) // 有结果就结束循环
							this.cozeMessageListStandards(conversation_id, chat_id)
						}
					})
				}, 3000) // 每秒轮询一次
			},
			//查看对话消息详情
			cozeMessageListStandards(conversation_id, chat_id) {
				this.HttpUtils.getcoze('/v3/chat/message/list', {
					conversation_id: conversation_id,
					chat_id: chat_id
				}).then(result => {
					console.log('结束', result)
					var downloadUrl = this.extractDownloadUrl(result.data.data)
					this.generationResults[1].url = downloadUrl || '';
					this.$notify({
						title: '成功',
						message: '课标已生成，可下载！',
						type: 'success',
						duration: 5000,
					});
				})
			},
			/***************************** 调用课标智能体结束 ***************************************/

			/***************************** 调用教案智能体开始 ***************************************/
			async callCozeTeachingAgent(fileId) {
				// 构建请求数据 - 使用通用提示词
				const testData = {
					"stream": false,
					"user_id": "9000001",
					"bot_id": "7558106196646019098",
					"enable_card": false,
					"publish_status": "published_online",
					"additional_messages": [{
						"role": "user",
						"type": "question",
						"content_type": "object_string",
						"meta_data": {},
						"content": `[{"type":"file","file_id":"${fileId}"},{"type":"text","text":"请根据我提供的文件内容，生成一份完整的教学教案。教案需要包含以下部分：教学目标、教学重点与难点、教学准备、教学过程、课堂活动设计、作业布置和教学反思。请确保教案内容专业、实用，并紧密围绕文件内容。"}]`
					}],
					"auto_save_history": true
				}
				const response = await this.HttpUtils.postcoze('/v3/chat', testData);
				console.log('扣子教学智能体', response.data.data)
				if (response.data.code == 0) {
					this.startPolling(response.data.data.conversation_id, response.data.data.id)
				} else {
					this.$message.error('错了哦');
				}
			},
			startPolling(conversation_id, chat_id) {
				const timer = setInterval(() => {
					//轮询查看对话详情status=='completed'
					this.HttpUtils.getcoze('/v3/chat/retrieve', {
						conversation_id: conversation_id,
						chat_id: chat_id
					}).then(result => {
						// 判断是否有结果（根据你的API实际返回结构调整条件）
						if (result && result.data.data.status == 'completed') { // 这里根据你的API返回结构调整
							clearInterval(timer) // 有结果就结束循环
							this.cozeMessageList(conversation_id, chat_id)
						}
					})
				}, 3000) // 每秒轮询一次
			},
			//查看对话消息详情
			cozeMessageList(conversation_id, chat_id) {
				this.HttpUtils.getcoze('/v3/chat/message/list', {
					conversation_id: conversation_id,
					chat_id: chat_id
				}).then(result => {
					console.log('结束', result)
					var downloadUrl = this.extractDownloadUrl(result.data.data)
					this.generationResults[2].url = downloadUrl || '';
					this.$notify({
						title: '成功',
						message: '教案已生成，可下载！',
						type: 'success',
						duration: 5000,
					});

					this.currentStep = 4;
					clearInterval(this.progressInterval);
					this.generationProgress = 100;
					this.generationStatus = "解析完成"
				})
			},
			/***************************** 调用教案智能体结束 ***************************************/

			extractDownloadUrl(messageList) {
				const answerMessage = messageList.find(item => item.type === "answer");
				if (!answerMessage) return null;

				const regex = /\[点我下载\]\((https?:\/\/[^\)]+)\)/;
				const match = answerMessage.content.match(regex);

				return match ? match[1] : null;
			},
			downloadFile(url) {
				if (url) {
					window.open(url, '_blank');
					this.resetState();
					// 清空上传组件的文件列表
					if (this.$refs.uploadRef) {
						this.$refs.uploadRef.clearFiles();
					}
				}
			},
			previewFile(item, isEdit) {
				var fullFilename = '',
					extension = '';
				if (item.url && item.url.includes('.')) {
					fullFilename = item.url.split('/').pop().split('?')[0];
					console.log(fullFilename); // "1990288566533632000.pptx"

					extension = fullFilename.split('.').pop();
					console.log(extension); // "pptx"
				} else {
					extension = '';
					fullFilename = ''
				}
				const route = this.$router.resolve({
					path: '/view-online',
					query: {
						url: item.url,
						type: extension,
						isEdit: isEdit,
						name: fullFilename,
						class: '2'
					}
				});

				window.open(route.href, '_blank');
			},
			previousStep() {
				if (this.currentStep > 1) {
					this.currentStep--
				}
			},
			nextStep() {
				if (!this.canProceed) return

				if (this.currentStep === 2) {
					console.log(this.materialOption)
					if (this.materialOption == 'default') { //默认教材
						this.getfile()
					}
					if (this.materialOption == 'upload') { //自定义上传
						this.generatePpt(1)
					}
					return
				}

				if (this.currentStep < 4) {
					this.currentStep++
				} else {
					this.resetForm()
				}
				this.generationResults[0].url='';
				this.generationResults[1].url='';
				this.generationResults[2].url='';
			},
			getNextButtonText() {
				switch (this.currentStep) {
					case 1:
					case 2:
						return '下一步 →'
					case 3:
						return '生成中...'
					case 4:
						return '重新开始'
					default:
						return '下一步 →'
				}
			},
			// 重置状态
			resetForm() {
				this.currentStep = 1
				this.selectedCourse = null
				this.materialOption = 'default'
				this.selectedFile = null;
				this.generationProgress = 0
				this.generationStatus = 'AI正在智能分析教材内容...'
			},
			getfile() {
				this.HttpUtils.post('/api/system/gjfilecategory/getfile', {
					filepath: this.filepath,
					preview: true
				}, {
					responseType: 'blob' // 强制返回 Blob
				}).then(res => {
					// console.log('文件流')
					// console.log('返回数据的类型:', typeof res.data); // 检查类型
					// console.log('具体构造:', res.data instanceof Blob ? '是 Blob' : '不是 Blob'); // 检查是否是 Blob
					// console.log('如果是 ArrayBuffer:', res.data instanceof ArrayBuffer ? '是 ArrayBuffer' :
					// 	'不是 ArrayBuffer');
					// 假设返回的数据是文件流，转换为 Blob
					const blob = new Blob([res.data], {
						type: 'application/pdf'
					});
					this.generatePpt(2, blob) //默认教材转成文件流
				})
			},
		}
	}
</script>

<style scoped>
	/deep/ .el-upload {
		width: 100%;
	}

	/deep/ .el-upload-dragger {
		width: 100%;
		border: none;
		background: none;
		height: auto;
	}

	.form-el-select {
		width: 100%;
	}

	/deep/.form-el-select .el-input__inner {
		border-radius: 12px;
		padding: 12px 16px;
		border: 2px solid #E5E7EB;
		height: 47px;
	}

	/* Hero Section */
	.lesson-prep-page .hero-section {
		text-align: center;
		margin-bottom: 60px;
	}

	.lesson-prep-page .hero-badge {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: white;
		padding: 8px 20px;
		border-radius: 100px;
		font-size: 14px;
		font-weight: 600;
		color: #2E5CE6;
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
		margin-bottom: 24px;
	}

	.lesson-prep-page .hero-badge-dot {
		width: 8px;
		height: 8px;
		background: #10B981;
		border-radius: 50%;
		animation: pulse 2s infinite;
	}

	@keyframes pulse {

		0%,
		100% {
			opacity: 1;
		}

		50% {
			opacity: 0.5;
		}
	}

	.lesson-prep-page .hero-title {
		font-size: 48px;
		font-weight: 800;
		line-height: 1.1;
		margin-bottom: 16px;
		background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		letter-spacing: -0.02em;
	}

	.lesson-prep-page .hero-subtitle {
		font-size: 18px;
		color: #6B7280;
		max-width: 600px;
		margin: 0 auto;
		line-height: 1.6;
	}

	/* Content Card */
	.lesson-prep-page .content-card {
		background: white;
		border-radius: 32px;
		box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
		overflow: hidden;
		border: 1px solid #E5E7EB;
	}

	.lesson-prep-page .content-header {
		background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
		padding: 32px;
		color: white;
		text-align: center;
	}

	.lesson-prep-page .content-title {
		font-size: 32px;
		font-weight: 800;
		margin-bottom: 8px;
	}

	.lesson-prep-page .content-subtitle {
		font-size: 16px;
		opacity: 0.9;
	}

	.lesson-prep-page .content-body {
		padding: 32px;
	}

	/* Step Form */
	.lesson-prep-page .step-form {
		display: flex;
		flex-direction: column;
		gap: 32px;
	}

	.lesson-prep-page .form-step {
		opacity: 0.6;
		pointer-events: none;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.lesson-prep-page .form-step.active {
		opacity: 1;
		pointer-events: auto;
	}

	.lesson-prep-page .form-step.completed {
		opacity: 0.8;
	}

	.lesson-prep-page .step-header {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 24px;
	}

	.lesson-prep-page .step-number {
		width: 40px;
		height: 40px;
		background: #E5E7EB;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 700;
		font-size: 18px;
		color: #6B7280;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.lesson-prep-page .form-step.active .step-number,
	.lesson-prep-page .form-step.completed .step-number {
		background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
		color: white;
	}

	.lesson-prep-page .step-title {
		font-size: 20px;
		font-weight: 700;
		color: #111827;
	}

	/* Form Elements */
	.lesson-prep-page .form-group {
		margin-bottom: 20px;
	}

	.lesson-prep-page .form-label {
		display: block;
		font-size: 14px;
		font-weight: 600;
		margin-bottom: 6px;
		color: #111827;
	}

	.lesson-prep-page .form-select {
		width: 100%;
		/* padding: 12px 16px;
		border: 2px solid #E5E7EB; */
		/* border-radius: 12px;
		font-size: 14px;
		font-family: inherit;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		background: white; */
	}

	.lesson-prep-page .form-select:focus {
		outline: none;
		border-color: #2E5CE6;
		box-shadow: 0 0 0 3px rgba(46, 92, 230, 0.1);
	}

	/* Course List */
	.lesson-prep-page .course-list {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
		gap: 16px;
	}

	.lesson-prep-page .course-item {
		background: #F9FAFB;
		border: 2px solid #E5E7EB;
		border-radius: 12px;
		padding: 20px;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		cursor: pointer;
	}

	.lesson-prep-page .course-item:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
		border-color: #60A5FA;
	}

	.lesson-prep-page .course-item.selected {
		border-color: #2E5CE6;
		background: rgba(46, 92, 230, 0.05);
		position: relative;
	}

	.lesson-prep-page .course-item.selected::after {
		content: '✓';
		position: absolute;
		top: 12px;
		right: 12px;
		width: 24px;
		height: 24px;
		background: #2E5CE6;
		color: white;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		font-weight: 700;
	}

	.lesson-prep-page .course-name {
		font-size: 16px;
		font-weight: 600;
		color: #111827;
		margin-bottom: 4px;
	}

	.lesson-prep-page .course-code {
		font-size: 12px;
		color: #6B7280;
		background: #E5E7EB;
		padding: 2px 8px;
		border-radius: 4px;
		display: inline-block;
	}

	.lesson-prep-page .course-desc {
		font-size: 14px;
		color: #6B7280;
		line-height: 1.4;
		margin: 12px 0;
	}

	.lesson-prep-page .course-stats {
		font-size: 12px;
		color: #9CA3AF;
	}

	/* Material Options */
	.lesson-prep-page .material-option {
		background: #F9FAFB;
		border: 2px solid #E5E7EB;
		border-radius: 12px;
		padding: 16px;
		margin-bottom: 12px;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.lesson-prep-page .material-option:hover {
		border-color: #60A5FA;
		background: rgba(46, 92, 230, 0.02);
	}

	.lesson-prep-page .material-option.selected {
		border-color: #2E5CE6;
		background: rgba(46, 92, 230, 0.05);
	}

	.lesson-prep-page .material-option-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;
	}

	.lesson-prep-page .material-option-title {
		font-size: 14px;
		font-weight: 600;
		color: #111827;
	}

	.lesson-prep-page .material-option-desc {
		font-size: 12px;
		color: #6B7280;
	}

	/* Upload Area */
	.lesson-prep-page .upload-area {
		border: 2px dashed #D1D5DB;
		border-radius: 16px;
		padding: 32px;
		text-align: center;
		background: #F9FAFB;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		cursor: pointer;
	}

	.lesson-prep-page .upload-area:hover {
		border-color: #2E5CE6;
		background: rgba(46, 92, 230, 0.02);
	}

	.lesson-prep-page .upload-icon {
		font-size: 48px;
		margin-bottom: 16px;
	}

	.lesson-prep-page .upload-text {
		font-size: 16px;
		font-weight: 600;
		color: #111827;
		margin-bottom: 8px;
	}

	.lesson-prep-page .upload-hint {
		font-size: 12px;
		color: #6B7280;
		margin-bottom: 16px;
	}

	/* Generation Area */
	.lesson-prep-page .generation-area {
		background: #f9fafb;
		border-radius: 24px;
		padding: 40px;
		text-align: center;
		border: 2px solid #E5E7EB;
	}

	.lesson-prep-page .generation-status {
		color: #111827;
		font-size: 24px;
		font-weight: 700;
		margin-bottom: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
	}

	.generation-status .el-icon-loading {
		font-size: 35px;
		font-weight: 700;
	}

	.lesson-prep-page .progress-container {
		margin-bottom: 32px;
	}

	.lesson-prep-page .progress-bar {
		width: 100%;
		height: 12px;
		background: #E5E7EB;
		border-radius: 100px;
		overflow: hidden;
		margin-bottom: 8px;
	}

	.lesson-prep-page .progress-fill {
		height: 100%;
		background: linear-gradient(90deg, #2E5CE6, #D97706);
		transition: width 0.3s ease;
	}

	.lesson-prep-page .progress-text {
		display: flex;
		justify-content: space-between;
		font-size: 14px;
		color: #6B7280;
	}

	.progress-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
	}

	.progress-details {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: 12px;
		font-size: 14px;
		color: var(--text-secondary);
	}

	.time-estimate {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.speed-indicator {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.progress-title {
		font-size: 18px;
		font-weight: 600;
		color: var(--text-primary);
	}


	.lesson-prep-page .generation-steps {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 20px;
		margin-top: 32px;
	}

	.lesson-prep-page .generation-step {
		background: white;
		border: 2px solid #E5E7EB;
		border-radius: 16px;
		padding: 24px 16px;
		position: relative;
	}

	.lesson-prep-page .generation-step.current {
		border-color: var(--primary);
		background: rgba(46, 92, 230, 0.05);
		opacity: 1;
		transform: scale(1.02);
		box-shadow: var(--shadow-md);
	}

	.lesson-prep-page .generation-step.completed {
		border-color: var(--success);
		background: rgba(16, 185, 129, 0.05);
		opacity: 1;
	}

	.lesson-prep-page .generation-step.completed::after {
		content: '✓';
		position: absolute;
		top: 8px;
		right: 8px;
		width: 20px;
		height: 20px;
		background: var(--success);
		color: white;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		font-weight: 700;
	}

	.lesson-prep-page .step-icon {
		font-size: 32px;
		display: block;
		margin-bottom: 12px;
		transition: var(--transition-base);
	}

	.lesson-prep-page .generation-step.current .step-icon {
		animation: bounce 2s infinite;
	}

	@keyframes bounce {

		0%,
		20%,
		53%,
		80%,
		100% {
			transform: translate3d(0, 0, 0);
		}

		40%,
		43% {
			transform: translate3d(0, -8px, 0);
		}

		70% {
			transform: translate3d(0, -4px, 0);
		}

		90% {
			transform: translate3d(0, -2px, 0);
		}
	}

	.lesson-prep-page .step-text {
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary);
		line-height: 1.4;
	}

	.generation-step.current .step-text {
		color: var(--primary);
	}

	.generation-step.completed .step-text {
		color: var(--success);
	}

	.step-time {
		font-size: 12px;
		color: var(--text-tertiary);
		margin-top: 8px;
		display: block;
	}

	.generation-step.current .step-time {
		color: var(--primary);
		font-weight: 600;
	}

	.generation-step.completed .step-time {
		color: var(--success);
	}

	/* AI Visualization */
	.ai-visualization {
		margin: 32px 0;
		padding: 24px;
		background: linear-gradient(135deg, rgba(46, 92, 230, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%);
		border-radius: 24px;
		border: 1px solid var(--border-light);
	}

	.ai-brain {
		width: 80px;
		height: 80px;
		margin: 0 auto 16px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 32px;
		color: white;
		position: relative;
		animation: pulse-brain 2s infinite;
	}

	@keyframes pulse-brain {

		0%,
		100% {
			transform: scale(1);
			box-shadow: 0 0 0 0 rgba(46, 92, 230, 0.7);
		}

		50% {
			transform: scale(1.05);
			box-shadow: 0 0 0 10px rgba(46, 92, 230, 0);
		}
	}

	.ai-status {
		text-align: center;
		font-size: 16px;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 8px;
	}

	.ai-description {
		text-align: center;
		font-size: 14px;
		color: var(--text-secondary);
		line-height: 1.5;
	}

	/* Results Grid */
	.lesson-prep-page .results-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 24px;
	}

	.lesson-prep-page .result-card {
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 16px;
		padding: 24px;
		text-align: center;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.lesson-prep-page .result-card:hover {
		transform: translateY(-4px);
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
	}

	.lesson-prep-page .result-icon {
		font-size: 48px;
		margin-bottom: 16px;
		display: block;
	}

	.lesson-prep-page .result-title {
		font-size: 20px;
		font-weight: 700;
		margin-bottom: 8px;
		color: #111827;
	}

	.lesson-prep-page .result-desc {
		font-size: 14px;
		color: #6B7280;
		line-height: 1.5;
		margin-bottom: 20px;
	}

	.lesson-prep-page .result-actions {
		display: flex;
		gap: 8px;
		justify-content: center;
	}

	/* Buttons */
	.lesson-prep-page .btn {
		padding: 12px 24px;
		border: none;
		border-radius: 12px;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	.lesson-prep-page .btn:hover {
		transform: translateY(-2px);
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
	}

	.lesson-prep-page .btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.lesson-prep-page .btn-primary {
		background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
		color: white;
	}

	.lesson-prep-page .btn-secondary {
		background: #F3F4F6;
		color: #111827;
		border: 1px solid #D1D5DB;
	}

	.lesson-prep-page .action-button {
		padding: 6px 12px;
		border: none;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.lesson-prep-page .action-button.primary {
		background: #2E5CE6;
		color: white;
	}

	.lesson-prep-page .action-button.secondary {
		background: #F3F4F6;
		color: #111827;
	}

	.lesson-prep-page .form-actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: 32px;
		padding-top: 24px;
		border-top: 1px solid #E5E7EB;
	}

	.lesson-prep-page .checkbox-group {
		margin-top: 16px;
	}

	.lesson-prep-page .checkbox-item {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	.lesson-prep-page .file-list {
		margin-top: 12px;
	}

	.lesson-prep-page .file-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 12px;
		background: #F3F4F6;
		border-radius: 6px;
		margin-bottom: 4px;
		font-size: 14px;
	}

	.lesson-prep-page .file-item button {
		background: none;
		border: none;
		font-size: 16px;
		cursor: pointer;
		color: #EF4444;
	}

	.lesson-prep-page .file-item button:hover {
		color: #DC2626;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.lesson-prep-page .hero-title {
			font-size: 32px;
		}

		.lesson-prep-page .course-list {
			grid-template-columns: 1fr;
		}

		.lesson-prep-page .results-grid {
			grid-template-columns: 1fr;
		}
	}
</style>